import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CreativeType, CreativeTypes } from '../../types';
import VideoAsset from './VideoAsset';
import ImageAsset from './ImageAsset';
import PlayableAsset from './PlayableAsset';
import YoutubeAsset from './YoutubeAsset';
import UnavailableCreative from '../../../components/creatives/UnavailableCreative';
import Icon, { IconSizes, IconTypes } from '../../../components/foundations/Icon';
import css from './CreativeAsset.css';

export const ERROR_TEXT = 'STATIC.CREATIVES.UNABLE_TO_LOAD_PREVIEW';
export const GRAPHIC_CONTAINER_TEST_ID = 'graphicContainer';
export const UNAUTHORIZED_URL = 'https://cdn.singular.net/dashboard/creatives/a5cf19bf8927094f2c9c73a597114c052599acd3';
export const UNAVAILABLE_TEXT = 'STATIC.CREATIVES.NO_GRAPHIC_AVAILABLE';

const CreativeTypeIconMap = {
    [CreativeTypes.VIDEO]: 'video-fill',
    [CreativeTypes.IMAGE]: 'pic-fill',
    [CreativeTypes.PLAYABLE]: 'hand-finger-2-fill',
    [CreativeTypes.GIF]: 'gif',
};

const CreativeAsset = React.forwardRef(
    (
        {
            type,
            url,
            embeddingUrl,
            onLoad,
            onError: onErrorProp,
            controls = false,
            aspectRatio,
            className,
            showCreativeTypeIndication = false,
            muted = true,
            showPlayable = true,
        },
        ref
    ) => {
        const [broken, setBroken] = useState(false);
        const [youtubeFailed, setYoutubeFailed] = useState(false);

        useEffect(() => {
            url === UNAUTHORIZED_URL && onErrorProp && onErrorProp();
        }, [url, onErrorProp]);

        const onError = useCallback(() => {
            setBroken(true);
            onErrorProp && onErrorProp();
        }, [onErrorProp]);

        const onYouTubeError = useCallback(() => {
            setYoutubeFailed(true);
        }, []);

        const getAssetElement = () => {
            if (url === UNAUTHORIZED_URL || broken) {
                // If the asset failed to load, but we have a YouTube embedding URL, show YouTube video iframe.
                if (broken && embeddingUrl && !youtubeFailed) {
                    return <YoutubeAsset url={embeddingUrl} onLoad={onLoad} onError={onYouTubeError} ref={ref} />;
                }

                return (
                    <UnavailableCreative
                        className={css.unavailableCreative}
                        iconSize={IconSizes.X_LARGE}
                        text={broken ? ERROR_TEXT : UNAVAILABLE_TEXT}
                    />
                );
            }

            const commonProps = { url, onLoad, onError, ref };

            if (type === CreativeTypes.PLAYABLE) return <PlayableAsset {...commonProps} showPlayable={showPlayable} />;

            if (type === CreativeTypes.VIDEO)
                return (
                    <VideoAsset
                        {...commonProps}
                        controls={controls}
                        muted={muted}
                        showCreativeTypeIndication={showCreativeTypeIndication}
                        showPlayable={showPlayable}
                    />
                );

            return <ImageAsset {...commonProps} />;
        };

        return (
            <div
                className={classNames(css.creativeView, className)}
                data-testid={GRAPHIC_CONTAINER_TEST_ID}
                style={{ aspectRatio }}
            >
                {showCreativeTypeIndication && (
                    <Icon
                        type={type === CreativeTypes.GIF ? IconTypes.TABLER : IconTypes.MINGCUTE}
                        name={CreativeTypeIconMap[type || CreativeTypes.IMAGE]}
                        size={IconSizes.SMALL}
                        className={classNames(css.creativeType, css.icon)}
                    />
                )}
                {getAssetElement()}
            </div>
        );
    }
);

CreativeAsset.propTypes = {
    style: PropTypes.shape({ aspectRatio: PropTypes.number }),
    type: CreativeType.type,
    url: CreativeType.url,
    embeddingUrl: PropTypes.string,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    controls: PropTypes.bool,
    aspectRatio: PropTypes.number,
    className: PropTypes.string,
    showCreativeTypeIndication: PropTypes.bool,
    muted: PropTypes.bool,
    showPlayable: PropTypes.bool,
};

export default CreativeAsset;
